import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { humanDatePattern } from './dateFormat';
import {
  DEFAULT_DATE_FORMAT,
  formatDate,
  formatDateTime,
  formatTime,
  formatTimestamp,
  type DateSettings,
} from './formatDate';

/** The one `['settings']` query — every page that needs platform settings reads this. */
export function useSettings() {
  return useQuery({ queryKey: ['settings'], queryFn: api.getSettings, staleTime: 60_000 });
}

/**
 * Date formatters bound to the platform's timezone and pattern. Before the
 * settings arrive they format in the browser's zone with the default pattern,
 * so a list never shows raw ISO strings while loading.
 */
export function useFormatDate() {
  const { data } = useSettings();
  const tz = data?.platform.timezone;
  const pattern = data?.platform.dateFormat ?? DEFAULT_DATE_FORMAT;
  return useMemo(() => {
    const settings: DateSettings = { timezone: tz, dateFormat: pattern };
    return {
      settings,
      /** `DD/MM/YYYY`-style placeholder for previews. */
      pattern: humanDatePattern(pattern),
      date: (v: string | number | Date) => formatDate(v, settings),
      dateTime: (v: string | number | Date) => formatDateTime(v, settings),
      timestamp: (v: string | number | Date) => formatTimestamp(v, settings),
      time: (v: string | number | Date) => formatTime(v, settings),
    };
  }, [tz, pattern]);
}
